import axios from "axios";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Dialog, DialogContent, CircularProgress } from "@mui/material";
import { Edit } from "lucide-react";
import AddEndpointForm from "./AddEndpointForm";
import ErrorNotification from "../../ErrorNotification";

const apiUrl = process.env.REACT_APP_API_URL;

export default function EndpointDetailsTable({ error, setError }) {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0 });

  console.log(editData);
  let accessToken = useSelector((state) => state.accessToken.value);

  const getData = async () => {
    setLoading(true);
    const query = `
    query MyQuery {
    getEndpointDetails {
    id
      ApiType
      EndpointName
      GroupName
      EndpointUrl
      Environment
      OemName
      ProductName
      UserName
    }
  }
  `;
    await axios
      .post(
        apiUrl,
        {
          query: query,
        },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      )
      .then((response) => {
        if (response.data.data && response.data.data.getEndpointDetails)
          setData(response.data.data.getEndpointDetails);
        if (
          response.data.errors &&
          response.data.errors[0].errorType == "Unauthorized"
        )
          setError("Unauthorized");
      })
      .catch((error) => {
        console.log(error);
      });
    setLoading(false);
  };

  useEffect(() => {
    getData();
  }, [refresh]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setEditData({
      UserName: row.UserName,
      Password: row.Password,
      EndpointUrl: row.EndpointUrl,
    });
  };

  const handleSubmit = async (id) => {
    const query = `
      mutation MyMutation( $EndpointUrl:String, $id:String, $UserName:String,$Password:String ) {
  updateEndpointDetails(
    EndpointUrl:  $EndpointUrl
    id: $id
    Password:  $Password
    UserName:  $UserName
  )
}

    `;

    const variables = {
      id: id,
      UserName: editData.UserName,
      Password: editData.Password,
      EndpointUrl: editData.EndpointUrl,
    };

    try {
      const response = await axios.post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      if (
        response.data.errors &&
        response.data.errors[0].errorType == "Unauthorized"
      )
        setError(
          "Edit access is not granted to your account for this resource."
        );
      setRefresh(!refresh);
    } catch (error) {
      console.log(error);
    }

    setEditingId(null);
    setEditData({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleChange = (e, field) => {
    setEditData({
      ...editData,
      [field]: e.target.value,
    });
  };

  const displayData = showAll ? data : data.slice(0, 10);

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200">
      {error && (
        <ErrorNotification message={error} onClose={() => setError(null)} />
      )}
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-[#003366]">
            Endpoints ({data ? data.length : 0})
          </h3>
          <button
            onClick={handleClickOpen}
            className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Endpoint
          </button>
        </div>

        <div className=" overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#003366]/5">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                  Endpoint
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                  Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                  Environment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                  Oem
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                  Url
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                  Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                  Password
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-[#003366] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayData.map((row, i) => (
                <tr key={i} className="hover:bg-[#003366]/5">
                  <td className="px-6 py-4 whitespace-nowrap text-[#003366]">
                    {row.EndpointName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                    {row.ApiType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                    {row.GroupName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                    {row.Environment}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                    {row.OemName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                    {row.ProductName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                    {editingId === row.id ? (
                      <input
                        type="text"
                        value={editData.EndpointUrl || ""}
                        onChange={(e) => handleChange(e, "EndpointUrl")}
                        className="border rounded px-2 py-1 w-full"
                      />
                    ) : (
                      row.EndpointUrl
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                    {editingId === row.id && row.UserName ? (
                      <input
                        type="text"
                        value={editData.UserName || ""}
                        onChange={(e) => handleChange(e, "UserName")}
                        className="border rounded px-2 py-1 w-full"
                      />
                    ) : (
                      row.UserName
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                    {editingId === row.id && row.UserName ? (
                      <input
                        type="text"
                        value={editData.Password || ""}
                        onChange={(e) => handleChange(e, "Password")}
                        className="border rounded px-2 py-1 w-full"
                      />
                    ) : (
                      row.UserName && "[protected]"
                    )}
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-gray-700">
                    {editingId === row.id ? (
                      <div className="flex space-x-2 justify-center">
                        <button
                          onClick={() => handleSubmit(row.id)}
                          className={
                            editData.UserName &&
                              (!editData.Password || editData.Password == "")
                              ? "text-gray-600"
                              : `text-green-600 hover:text-green-800 font-medium`
                          }
                          disabled={
                            editData.UserName &&
                            (!editData.Password || editData.Password == "")
                          }
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancel}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEdit(row)}
                        className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip(
                            (row.ApiType == "AUTH" ||
                              row.ApiType == "auth") && {
                              visible: true,
                              x: rect.left + rect.width / 2,
                              y: rect.bottom + 5,
                            }
                          );
                        }}
                        onMouseLeave={() =>
                          setTooltip({ visible: false, x: 0, y: 0 })
                        }
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && data.length > 4 && (
            <div className="text-center mt-4">
              <button
                onClick={() => setShowAll(!showAll)}
                className="text-[#003366] hover:text-[#004488] font-medium"
              >
                {showAll ? "Show Less" : "Show More"}
              </button>
            </div>
          )}
          {loading && (
            <div className="flex justify-center items-center py-16">
              <CircularProgress size={40} style={{ color: "#003366" }} />
            </div>
          )}
          {!loading && data.length === 0 && (
            <div className="text-center py-12">
              <div className="text-[#003366]">
                <svg
                  className="w-12 h-12 mx-auto mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 12H4"
                  />
                </svg>
                <p className="text-lg font-semibold">No Data Available</p>
                <p className="mt-2">
                  Click the Add New button to add your first item
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {tooltip.visible && (
        <div
          style={{
            position: "fixed",
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translateX(-50%)",
            zIndex: 1000,
          }}
          className="bg-black text-white text-sm rounded px-2 py-1"
        >
          A password must be provided when making edits
        </div>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent className="p-0">
          <AddEndpointForm
            close={handleClose}
            refresh={refresh}
            setRefresh={setRefresh}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
